/**
 * Grouped Quick Leads list — mirrors the Current Leads tab visually:
 *   • Unassigned section at top
 *   • Property-grouped sections below (collapsible)
 *   • QuickLeadRow per row (same shape as LeadRow but contact-tracking
 *     replaces the strength + status row)
 *   • Click a row → opens LeadFormDialog in `kind="quick"` view-mode
 *
 *
 */
import { useEffect, useState } from 'react';
import {
  getQuickLeads, createQuickLead, updateQuickLead, deleteQuickLead,
  getProperties, getUnits, moveQuickToCurrent,
} from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Building2, ChevronDown, ChevronRight, AlertCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { QuickLeadRow } from '@/components/leads/QuickLeadRow';
import { LeadFormDialog } from '@/components/leads/LeadFormDialog';
import { LeadAgent } from '@/components/leads/LeadAgent';

const emptyForm = {
  name: '', source: '', phone: '', email: '',
  desired_start_date: '', desired_end_date: '',
  potential_unit_ids: [], pets: '', parking_request: '',
  converted_to_tenant: false, tenant_id: '',
  price_offered: '', preferred_contact_method: '', notes: '',
  is_unassigned: false, unassigned_note: '',
  emailed_dates: [], called_dates: [], texted_dates: [], last_contact_date: '',
};

// Map between the dialog's internal shape (uses `potential_unit_ids` from
// the unified LeadFormDialog) and the quick-lead document's `best_unit_ids`.
function quickLeadToForm(q) {
  return {
    ...emptyForm,
    id: q.id,
    name: q.name || '', source: q.source || '',
    phone: q.phone || '', email: q.email || '',
    desired_start_date: q.desired_start_date || '',
    desired_end_date: q.desired_end_date || '',
    potential_unit_ids: q.best_unit_ids || [],
    pets: q.pets || '', parking_request: q.parking_request || '',
    converted_to_tenant: q.converted_to_tenant || false,
    tenant_id: q.tenant_id || '',
    price_offered: q.price_offered || '',
    preferred_contact_method: q.preferred_contact_method || '',
    notes: q.notes || '',
    is_unassigned: q.is_unassigned || false,
    unassigned_note: q.unassigned_note || '',
    emailed_dates: q.emailed_dates || [],
    called_dates: q.called_dates || [],
    texted_dates: q.texted_dates || [],
    last_contact_date: q.last_contact_date || '',
  };
}

function formToQuickPayload(form) {
  return {
    name: form.name, source: form.source,
    phone: form.phone, email: form.email,
    desired_start_date: form.desired_start_date || '',
    desired_end_date: form.desired_end_date || '',
    best_unit_ids: form.potential_unit_ids || [],
    pets: form.pets, parking_request: form.parking_request,
    price_offered: form.price_offered ? parseFloat(form.price_offered) : null,
    preferred_contact_method: form.preferred_contact_method,
    notes: form.notes,
    is_unassigned: !!form.is_unassigned,
    unassigned_note: form.unassigned_note,
    converted_to_tenant: !!form.converted_to_tenant,
    tenant_id: form.tenant_id || null,
    emailed_dates: form.emailed_dates || [],
    called_dates: form.called_dates || [],
    texted_dates: form.texted_dates || [],
    last_contact_date: form.last_contact_date || '',
  };
}

export function QuickLeadsList() {
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProps, setExpandedProps] = useState({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [defaultViewMode, setDefaultViewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [l, p, u] = await Promise.all([getQuickLeads(), getProperties(), getUnits()]);
      setLeads(l);
      setProperties(p);
      setAllUnits(u);
    } catch { toast.error('Failed to load quick leads'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const propMap = {};
  properties.forEach((p) => { propMap[p.id] = p; });
  const unitMap = {};
  allUnits.forEach((u) => { unitMap[u.id] = u; });

  const unassignedLeads = leads.filter((l) => l.is_unassigned || (l.best_unit_ids || []).length === 0);
  const leadsByProperty = {};
  leads.forEach((lead) => {
    if (lead.is_unassigned) return;
    const unitIds = lead.best_unit_ids || [];
    if (unitIds.length === 0) return;
    const seenProps = new Set();
    unitIds.forEach((uid) => {
      const unit = unitMap[uid];
      if (unit && !seenProps.has(unit.property_id)) {
        seenProps.add(unit.property_id);
        if (!leadsByProperty[unit.property_id]) leadsByProperty[unit.property_id] = [];
        leadsByProperty[unit.property_id].push(lead);
      }
    });
  });

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setDefaultViewMode(false); setDialogOpen(true);
  };
  const openDetail = (lead) => {
    setEditing(lead); setForm(quickLeadToForm(lead)); setDefaultViewMode(false); setDialogOpen(true);
  };

  // Open the Add Quick Lead dialog pre-filled with whatever the AI agent
  // could pull out of the pasted email / dropped image / PDF. The user
  // still gets to review, tweak, and pick units before saving.
  const openCreateFromParsed = (parsed) => {
    if (!parsed) return;
    const numericBudget = parseFloat(String(parsed.budget || '').replace(/[^0-9.]/g, ''));
    const extraNotes = [];
    if (parsed.property_interest) extraNotes.push(`Interest: ${parsed.property_interest}`);
    if (parsed.budget && !Number.isFinite(numericBudget)) extraNotes.push(`Budget: ${parsed.budget}`);
    if (parsed.notes) extraNotes.push(parsed.notes);
    setEditing(null);
    setForm({
      ...emptyForm,
      name: parsed.name || '',
      phone: parsed.phone || '',
      email: parsed.email || '',
      desired_start_date: parsed.desired_start_date || '',
      desired_end_date: parsed.desired_end_date || '',
      price_offered: Number.isFinite(numericBudget) ? String(numericBudget) : '',
      notes: extraNotes.join('\n'),
    });
    setDefaultViewMode(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = formToQuickPayload(form);
    try {
      if (editing) { await updateQuickLead(editing.id, payload); toast.success('Quick lead updated'); }
      else { await createQuickLead(payload); toast.success('Quick lead created'); }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  const handleSaveUnassigned = async () => {
    setSaving(true);
    const payload = { ...formToQuickPayload(form), is_unassigned: true, best_unit_ids: [] };
    try {
      if (editing) await updateQuickLead(editing.id, payload);
      else await createQuickLead(payload);
      toast.success('Saved as unassigned');
      setDialogOpen(false); fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await deleteQuickLead(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  // Auto-saves contact-log changes (emailed/called/texted dates) without
  // requiring the user to click Edit → Update first. Fires from
  // ContactTrackingSection on every add / edit / remove.
  const handleContactLogAutosave = async (fields) => {
    if (!editing?.id) return;
    try {
      await updateQuickLead(editing.id, fields);
      // Refresh the list silently so row summaries stay in sync.
      fetchData();
    } catch { toast.error('Failed to save contact log'); }
  };

  const handleMoveToCurrent = (lead) => {
    toast(`Move "${lead.name || 'this lead'}" to Current Leads?`, {
      description: 'Last-contact log will be cleared on the move.',
      action: { label: 'Move', onClick: async () => {
        try { await moveQuickToCurrent(lead.id); toast.success('Moved to Current Leads'); fetchData(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
      } },
      cancel: { label: 'Cancel', onClick: () => {} },
      duration: 8000,
    });
  };

  const handleConvert = (lead) => {
    const convertData = {
      name: lead.name, phone: lead.phone, email: lead.email,
      move_in_date: lead.desired_start_date || '',
      move_out_date: lead.desired_end_date || '',
      potential_unit_ids: lead.best_unit_ids,
    };
    window.localStorage.setItem('convert_lead', JSON.stringify({ ...convertData, lead_id: lead.id }));
    window.location.href = '/tenants';
  };

  const rowHandlers = {
    onOpenDetail: openDetail,
    onMoveToCurrent: handleMoveToCurrent,
    onDelete: handleDelete,
    onConvert: handleConvert,
    onUpdated: fetchData,
  };

  if (loading) return <div className="flex items-center justify-center h-40 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="quick-leads-list">
      <Card className="border-dashed border-blue-200 bg-blue-50/20" data-testid="quick-leads-agent-card">
        <CardContent className="p-3 space-y-2">
          <p className="text-[10px] font-semibold text-blue-700/70 uppercase tracking-wider">
            Lead Agent — drop an image / PDF, or paste an email
          </p>
          <LeadAgent onLeadParsed={openCreateFromParsed} />
        </CardContent>
      </Card>

      <div className="flex items-center justify-end">
        <Button size="sm" className="h-8 text-xs" onClick={openCreate} data-testid="quick-leads-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Quick Lead
        </Button>
      </div>

      {leads.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No quick leads yet. Use <strong>Add Quick Lead</strong> to start tracking, or move one from Current Leads.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {unassignedLeads.length > 0 && (
            <div className="space-y-3">
              <h2 className="font-heading text-base font-semibold tracking-tight flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                Unassigned Quick Leads
                <Badge variant="secondary" className="text-xs tabular-nums">{unassignedLeads.length}</Badge>
              </h2>
              <Card className="overflow-hidden border-amber-200/50">
                <div className="space-y-2 p-4">
                  {unassignedLeads.map((lead) => (
                    <QuickLeadRow key={lead.id} lead={lead} unitMap={unitMap} {...rowHandlers} />
                  ))}
                </div>
              </Card>
            </div>
          )}

          {Object.entries(leadsByProperty).map(([propId, propLeads]) => {
            const prop = propMap[propId];
            const isOpen = expandedProps[propId] !== false;
            return (
              <div key={propId} className="space-y-3">
                <Collapsible open={isOpen} onOpenChange={() => setExpandedProps((prev) => ({ ...prev, [propId]: !isOpen }))}>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <h2 className="font-heading text-base font-semibold tracking-tight">{prop?.name || 'Unknown Property'}</h2>
                      <Badge variant="secondary" className="text-xs tabular-nums">{propLeads.length}</Badge>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Card className="mt-2 overflow-hidden">
                      <div className="space-y-2 p-4">
                        {propLeads.map((lead) => (
                          <QuickLeadRow key={lead.id} lead={lead} unitMap={unitMap} {...rowHandlers} />
                        ))}
                      </div>
                    </Card>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </div>
      )}

      <LeadFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        onSaveAsUnassigned={handleSaveUnassigned}
        saving={saving}
        onLeadUpdated={fetchData}
        onContactLogAutosave={handleContactLogAutosave}
        defaultViewMode={defaultViewMode}
        kind="quick"
      />
    </div>
  );
}
